import { Button, ProgressBar } from '@arcadiapower/shrike';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OneColumnGrid } from '~/components/layout';
import { Title, Description } from '~/components/typography';
import {
  fetchDocuSignEnvelopes,
  fetchFunnelPacket,
  fetchVnmTerms,
  requestDocuSignEnvelope,
} from '~/services';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import {
  DOCU_SIGN_POLLING_INTERVAL,
  INTERNAL_ROUTES,
  SPONSORED_SOLAR_ROUTES,
} from '~/utils/constants';
import { getSavingsRateDescription } from '~/utils/funnels';
import { getSubRoute } from '~/utils/redirects';
import { store } from '~/utils/store';
import { ReviewForm, ReviewFormProps } from './review-form.component';

type ReviewFormTypeProps = {
  funnelPacket: any;
  contracts: any;
  email: string;
  firstName: string;
  lastName: string;
  lmiStatus: string;
  paymentMethod: any;
  phoneNumber: string;
  promoCode: string;
  solarSavingsRate: string;
  utility: any;
};

const ReviewFormLoader = ({ copy }) => {
  return (
    <OneColumnGrid textAlign="center" testId="screen-DocuSign">
      <Title>{copy.title}</Title>
      <Description mt={6}>{copy.description}</Description>
      <ProgressBar maxValue={1000 * 180} secondsToProgress={3} automatic />
    </OneColumnGrid>
  );
};

export const TimeOutMessage = ({ copy, navigate }) => {
  return (
    <OneColumnGrid textAlign="center" testId="screen-DocuSign">
      <Title>{copy.title}</Title>
      <Description mt={6}>{copy.description}</Description>
      <Button
        mode="primary"
        onClick={() => {
          navigate(
            getSubRoute(
              INTERNAL_ROUTES.SPONSORED_SOLAR,
              SPONSORED_SOLAR_ROUTES.REVIEW_AND_VIEW_TERMS,
            ),
          );
        }}
      >
        {copy.buttonText}
      </Button>
    </OneColumnGrid>
  );
};

export const redirectToDocuSign = docuSignEnvelopes => {
  const envelope = docuSignEnvelopes.find(d => d.status === 'sent');
  if (envelope && envelope.url) {
    window.open(envelope.url, '_self');
  } else {
    const completedEnvelopes = docuSignEnvelopes.find(c =>
      ['signed', 'completed'].includes(c.status!),
    );
    if (completedEnvelopes) {
      const nextStage = `${window.location.origin}${getSubRoute(
        INTERNAL_ROUTES.SPONSORED_SOLAR,
        SPONSORED_SOLAR_ROUTES.REVIEW_AND_VIEW_TERMS,
      )}`;
      window.open(nextStage, '_self');
    }
  }
};

export const ReviewFormWrapper = () => {
  const [reviewFormData, setReviewFormData] = useState<ReviewFormTypeProps>(
    {} as ReviewFormTypeProps,
  );
  const [, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [polling, setPolling] = useState(false);
  const userId = store.get('USER_ID')!;
  const navigate = useNavigate();
  const MAX_RETRIES = 4;

  useEffect(() => {
    if (!userId) {
      navigate(INTERNAL_ROUTES.EXPIRED_SESSION, {
        state: { url: INTERNAL_ROUTES.SPONSORED_SOLAR },
      });
    }
  }, [userId]);

  const handleRedirection = () => {
    const pollFunction = async () => {
      try {
        setPolling(true);
        setTimedOut(false);
        await fetchEnvelopesWithRetry(userId);
      } catch (error) {
        setError('Error fetching data. Please try again.');
      }
    };
    pollFunction();
  };

  const fetchEnvelopesWithRetry = async (userId: number, retries: number = 0): Promise<any> => {
    const docuSignEnvelopes = await fetchEnvelopes(userId);
    if (docuSignEnvelopes.length > 0) {
      redirectToDocuSign(docuSignEnvelopes);
      return docuSignEnvelopes;
    } else if (retries < MAX_RETRIES) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(fetchEnvelopesWithRetry(userId, retries + 1));
        }, DOCU_SIGN_POLLING_INTERVAL);
      });
    } else {
      setTimedOut(true);
      setPolling(false);
      return null;
    }
  };

  const fetchEnvelopes = async (userId: number) => {
    const { docuSignEnvelopes } = await fetchDocuSignEnvelopes({
      userId,
      returnUrl: `${window.location.origin}${getSubRoute(
        INTERNAL_ROUTES.SPONSORED_SOLAR,
        SPONSORED_SOLAR_ROUTES.REVIEW_AND_VIEW_TERMS,
      )}`,
    });
    return docuSignEnvelopes;
  };

  const fetchSolarProjects = async (funnelPacket: FetchFunnelPacketResponse) => {
    if (!funnelPacket) {
      return;
    }
    const terms = await fetchVnmTerms({
      stateId: funnelPacket.utility.state.id,
      utilityAccountId: funnelPacket.utilityAccount.id,
    });
    terms.docuSignContracts = terms.docuSignContracts.filter(
      contract => contract.contractType === 'lmi',
    );

    return terms.docuSignContracts;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const funnelPacket = await fetchFunnelPacket({ userId });
        const solarProjects = await fetchSolarProjects(funnelPacket);

        const data = {
          ...reviewFormData,
          funnelPacket,
          contracts: solarProjects,
        };
        // Generates a DocuSign envelope for the user
        await requestDocuSignEnvelope({
          contractVersionIds: data?.contracts?.map(cv => cv.id),
          userId,
          utilityAccountId: funnelPacket.utilityAccount.id,
        });

        setReviewFormData(data);
        setError(null);
      } catch (error) {
        setError('Error fetching data. Please try again.');
      }
    };

    fetchData();
  }, []);

  const props: ReviewFormProps = {
    context: {
      funnelPacket: reviewFormData?.funnelPacket,
      contracts: reviewFormData?.contracts,
      email: reviewFormData?.funnelPacket?.user.email,
      firstName: reviewFormData?.funnelPacket?.user.firstName,
      lastName: reviewFormData?.funnelPacket?.user.lastName,
      phoneNumber: reviewFormData?.funnelPacket?.user.phoneNumber,
      promoCode: reviewFormData?.funnelPacket?.user.promoCode,
      solarSavingsRate: getSavingsRateDescription('OH'),
      utility: reviewFormData?.funnelPacket?.utility,
    },
    flags: {
      showSelfAttestationForm: false,
      showLmiOptions: false,
      requiresAddressFields: false,
    },
    loading: !(reviewFormData.funnelPacket && reviewFormData.contracts),
    next: handleRedirection,
  };

  const loaderCopy = {
    title: "Don't close this window yet, we're preparing your income status verification form...",
    description: '',
  };

  const timeOutCopy = {
    title: 'One final step!',
    description:
      "It's taking longer than expected to prepare your income status verification form, so rather than have you wait we'll email you once it's ready.",
    buttonText: 'Continue',
  };

  if (!polling && !timedOut) {
    return <ReviewForm {...props} />;
  } else if (timedOut) {
    return <TimeOutMessage copy={timeOutCopy} navigate={navigate} />;
  }
  return <ReviewFormLoader copy={loaderCopy} />;
};
